import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, Pencil, Trash2, Upload, Network } from "lucide-react";
import { BU } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";

export default function BusinessUnits() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bus, setBus] = useState([]);
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, u, d] = await Promise.all([
        api.get("/business-units"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/departments").catch(() => ({ data: [] })),
      ]);
      setBus(b.data); setUsers(u.data); setDepts(d.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const owner = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const deptCount = (id) => depts.filter((d) => d.business_unit_id === id).length;
  const userCount = (id) => users.filter((u) => u.business_unit_id === id).length;

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/business-units/${data.id}`, data);
      else await api.post("/business-units", data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this business unit? Position Profiles inside will be orphaned.")) return;
    try { await api.delete(`/business-units/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canCreateBU = user?.role === "super_admin";
  // Anyone who can edit templates (BU Manager, BU Owner, Super Admin) can launch imports.
  const canImportTemplate = ["super_admin", "bu_owner", "bu_manager"].includes(user?.role);
  // BU Owners + Managers are scoped to their own BU when importing.
  const importableBUs = user?.role === "super_admin"
    ? bus
    : bus.filter((b) => b.id === user?.business_unit_id);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Business Units</div>
          <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Org structure
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Position Profiles, templates and reviews are organised by Business Unit.</p>
        </div>
        <div className="flex items-center gap-2">
          {canImportTemplate && (
            <button
              data-testid="bu-import-template-btn"
              onClick={() => setImporting(true)}
              className="border border-zinc-300 px-4 py-2 rounded-sm text-sm font-medium hover:bg-zinc-50 flex items-center gap-2"
            >
              <Upload className="w-4 h-4" /> Import template
            </button>
          )}
          {canCreateBU && (
            <button data-testid={BU.newBtn}
              onClick={() => setEditing({ name: "", description: "", owner_id: null })}
              style={{ background: "var(--brand-primary)" }}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New business unit
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch" data-testid="bu-grid">
        {loading ? (
          <>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} data-testid={`bu-card-skeleton-${i}`}
                   className="border border-zinc-200 bg-white p-5 flex flex-col h-full animate-pulse">
                <div className="flex items-start justify-between gap-3 h-12">
                  <div className="w-12 h-12 rounded-sm bg-zinc-100" />
                  <div className="w-16 h-4 rounded-sm bg-zinc-100" />
                </div>
                <div className="mt-3 flex-1 flex flex-col gap-2">
                  <div className="h-5 w-2/3 bg-zinc-100 rounded-sm" />
                  <div className="h-3 w-full bg-zinc-100 rounded-sm" />
                  <div className="h-3 w-4/5 bg-zinc-100 rounded-sm" />
                  <div className="mt-auto pt-3 border-t border-zinc-100 space-y-1">
                    <div className="h-3 w-full bg-zinc-100 rounded-sm" />
                    <div className="h-3 w-full bg-zinc-100 rounded-sm" />
                    <div className="h-3 w-2/3 bg-zinc-100 rounded-sm" />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
        {user?.role === "super_admin" && (
          <Link
            to="/global"
            data-testid="bu-card-global"
            className="border-2 border-dashed border-zinc-900 bg-white p-5 hover:shadow-md transition-all flex flex-col h-full"
            style={{ background: "linear-gradient(180deg, #fff 0%, #f4f4f5 100%)" }}
          >
            <div className="flex items-start justify-between gap-3 h-12">
              <div
                className="w-12 h-12 rounded-sm flex items-center justify-center text-white shrink-0"
                style={{ background: "var(--brand-primary, #2C7BB6)" }}
              >
                <Network className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-zinc-900 text-white border-zinc-900">
                Platform
              </span>
            </div>
            <div className="mt-3 flex-1 flex flex-col">
              <div className="font-semibold text-lg">Global</div>
              <div className="text-xs text-zinc-500 mt-1 line-clamp-2">
                Platform-wide organogram, approvals, audit log and your signature queue — always available.
              </div>
              <div className="mt-auto pt-3 text-xs text-zinc-600">Open the Global workspace →</div>
            </div>
          </Link>
        )}
        {bus.map((b) => (
          <div key={b.id} className="border border-zinc-200 bg-white p-5 hover:border-zinc-400 transition-colors flex flex-col h-full">
            <div className="flex items-start justify-between gap-3 h-12">
              {b.logo ? (
                <img
                  src={b.logo}
                  alt={`${b.name} logo`}
                  data-testid={`bu-card-logo-${b.id}`}
                  className="h-12 max-w-[140px] w-auto object-contain"
                />
              ) : (
                <div className="w-12 h-12 rounded-sm bg-zinc-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-zinc-400" strokeWidth={1.5} />
                </div>
              )}
              {canCreateBU && (
                <div className="flex gap-2">
                  <button data-testid={`bu-edit-${b.id}`} onClick={() => setEditing(b)} className="text-zinc-500 hover:text-zinc-900"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`bu-delete-${b.id}`} onClick={() => del(b.id)} className="text-red-500 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <Link to={`/business-units/${b.id}`} className="block mt-3 flex-1 flex flex-col" data-testid={`bu-card-${b.id}`}>
              <div className="font-semibold text-lg">{b.name}</div>
              <div className="text-xs text-zinc-500 mt-1 line-clamp-2 min-h-[32px]">{b.description || "—"}</div>
              <div className="mt-auto pt-3 border-t border-zinc-100 space-y-1 text-xs text-zinc-600">
                <div className="flex justify-between">
                  <span>Owner</span>
                  <b className="text-zinc-800" data-testid={`bu-card-owner-${b.id}`}>{owner(b.owner_id)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Position Profiles</span>
                  <b className="text-zinc-800 tabular-nums" data-testid={`bu-card-profiles-${b.id}`}>{deptCount(b.id)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Users</span>
                  <b className="text-zinc-800 tabular-nums" data-testid={`bu-card-users-${b.id}`}>{userCount(b.id)}</b>
                </div>
              </div>
            </Link>
          </div>
        ))}
        {bus.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 text-sm py-16 border border-dashed border-zinc-300">
            No business units yet.
          </div>
        )}
          </>
        )}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit business unit" : "New business unit"} onClose={() => setEditing(null)}>
          <Form initial={editing} users={users.filter((u) => u.role === "bu_owner" || u.role === "super_admin")} onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}

      {importing && (
        <Modal title="Import KPA template from Excel" onClose={() => setImporting(false)}>
          <GlobalImportForm
            bus={importableBUs}
            depts={depts}
            onCancel={() => setImporting(false)}
            onImported={(tid) => { setImporting(false); nav(`/templates/${tid}/edit`); }}
          />
        </Modal>
      )}
    </div>
  );
}

function Form({ initial, users, onCancel, onSave }) {
  const [v, setV] = useState(initial);
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Name</label>
        <input data-testid="bu-name-input" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.name} onChange={(e) => setV({...v, name: e.target.value})} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Description</label>
        <input className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.description || ""} onChange={(e) => setV({...v, description: e.target.value})} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Owner (BU Owner)</label>
        <select data-testid="bu-owner-select" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.owner_id || ""} onChange={(e) => setV({...v, owner_id: e.target.value || null})}>
          <option value="">— Unassigned —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="bu-save-btn" onClick={() => onSave(v)} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Save</button>
      </div>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">{title}</div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function GlobalImportForm({ bus, depts, onCancel, onImported }) {
  const [buId, setBuId] = useState(bus[0]?.id || "");
  const buDepts = depts.filter((d) => d.business_unit_id === buId);
  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState(buDepts[0]?.id || "");
  const [fiscalYear, setFiscalYear] = useState(`FY${new Date().getFullYear()}`);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // When BU changes, reset department to the first one in that BU.
  React.useEffect(() => {
    const list = depts.filter((d) => d.business_unit_id === buId);
    setDepartmentId(list[0]?.id || "");
  }, [buId, depts]);

  const fyOptions = (() => {
    const cur = new Date().getFullYear();
    const out = [];
    for (let y = cur - 1; y <= cur + 7; y++) out.push(`FY${y}`);
    return out;
  })();

  const submit = async () => {
    if (!buId) return toast.error("Pick a Business Unit");
    if (!departmentId) return toast.error("This BU has no Position Profiles yet — create one first.");
    if (!file) return toast.error("Choose a .xlsx file");
    if (!name) return toast.error("Enter a name for the template");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("department_id", departmentId);
      fd.append("fiscal_year", fiscalYear);
      const { data } = await api.post("/templates/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.rows?.length || 0} KPA rows`);
      onImported(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
        We will look for columns: <b>Perspective</b>, <b>KPA Strategic Goal</b>, <b>KPA</b>, <b>Perspective KPA Weight</b>, <b>Measurement Tool</b>, <b>Comments</b> — header names are case-insensitive.
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Business Unit</label>
        <select
          data-testid="global-import-bu-select"
          className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
          value={buId} onChange={(e) => setBuId(e.target.value)}
        >
          {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Position Profile</label>
        <select
          data-testid="global-import-dept-select"
          className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          disabled={buDepts.length === 0}
        >
          {buDepts.length === 0 ? (
            <option value="">No Position Profiles in this BU</option>
          ) : (
            buDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)
          )}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Template name</label>
        <input
          data-testid="global-import-name-input"
          className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Finance KPA Template"
        />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Fiscal year</label>
        <select
          className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
          value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)}
        >
          {fyOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Excel file (.xlsx)</label>
        <input
          data-testid="global-import-file-input"
          type="file" accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block mt-1 text-sm"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button
          data-testid="global-import-submit-btn"
          disabled={busy}
          onClick={submit}
          style={{ background: "var(--brand-primary)" }}
          className="px-4 py-2 text-sm text-white rounded-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Importing…" : "Import"}
        </button>
      </div>
    </div>
  );
}
